<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { createConsumer } from '@rails/actioncable';
import QRCode from 'qrcode';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import SettingsSectionCard from 'dashboard/components-next/Settings/SettingsSectionCard.vue';
import dinastiaWhatsappChannel from 'dashboard/api/channel/dinastiaWhatsappChannel';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const store = useStore();

const connectionStatus = ref('');
const qrImage = ref('');
const isLoading = ref(false);
const isActionLoading = ref(false);
const isReconnecting = ref(false);
const isSyncing = ref(false);
const actionCooldown = ref(false);
const phoneNumber = ref('');
const qrDialogRef = ref(null);
let pollTimer = null;
let autoCloseTimer = null;
let cableSubscription = null;
let cableConsumer = null;
let isInitialized = false;

// ── Low-level helpers (no dependencies) ────────────────────────────────

function channelId() {
  return props.inbox?.channel?.id || props.inbox?.channel_id;
}

async function processQrData(qr) {
  if (!qr) return;
  if (qr.startsWith('data:image/')) {
    qrImage.value = qr;
  } else {
    qrImage.value = await QRCode.toDataURL(qr, { width: 320, margin: 2 });
  }
}

function autoCloseDialog() {
  if (qrDialogRef.value) {
    if (autoCloseTimer) clearTimeout(autoCloseTimer);
    autoCloseTimer = setTimeout(() => {
      qrDialogRef.value?.close();
    }, 2500);
  }
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

function delay(ms) {
  return new Promise(resolve => {
    setTimeout(resolve, ms);
  });
}

// ── ActionCable (uses helpers above) ───────────────────────────────────

async function handleCableMessage(data) {
  switch (data.event) {
    case 'qr_update':
      await processQrData(data.data?.qr_code);
      break;
    case 'connection_update':
      connectionStatus.value = data.data?.status || data.status;
      if (data.data?.status === 'connected') {
        qrImage.value = '';
        stopPolling();
        autoCloseDialog();
      } else if (data.data?.status === 'disconnected') {
        stopPolling();
      } else if (data.data?.session_data?.qr) {
        await processQrData(data.data.session_data.qr);
      }
      break;
    case 'connection_message':
      if (data.data?.message) {
        useAlert(data.data.message);
      }
      break;
    default:
      break;
  }
}

function subscribeToChannel() {
  const chId = channelId();
  if (!chId) return;

  const { websocketURL = '' } = window.chatwootConfig || {};
  const wsUrl = websocketURL ? `${websocketURL}/cable` : undefined;
  cableConsumer = createConsumer(wsUrl);

  const userId = store.getters.getCurrentUserID;
  const currentUser = store.getters.getCurrentUser;
  const pubsubToken = currentUser?.pubsub_token;

  cableSubscription = cableConsumer.subscriptions.create(
    {
      channel: 'DinastiaWhatsappChannel',
      channel_id: chId,
      user_id: userId,
      pubsub_token: pubsubToken,
    },
    {
      received: receivedData => handleCableMessage(receivedData),
    }
  );
}

function unsubscribeFromChannel() {
  if (cableSubscription) {
    cableSubscription.unsubscribe();
    cableSubscription = null;
  }
  if (cableConsumer) {
    cableConsumer.disconnect();
    cableConsumer = null;
  }
}

// ── Status config ──────────────────────────────────────────────────────

const statusConfig = computed(() => {
  const configs = {
    connected: {
      color: 'bg-n-teal-9',
      pulseColor: 'bg-n-teal-9/40',
      textColor: 'text-n-teal-11',
      bgColor: 'bg-n-teal-3',
      label: t('INBOX_MGMT.DINASTIA_CONNECTION.CONNECTED'),
      icon: 'i-lucide-wifi',
      pulse: false,
    },
    disconnected: {
      color: 'bg-n-ruby-9',
      pulseColor: 'bg-n-ruby-9/40',
      textColor: 'text-n-ruby-11',
      bgColor: 'bg-n-ruby-3',
      label: t('INBOX_MGMT.DINASTIA_CONNECTION.DISCONNECTED'),
      icon: 'i-lucide-wifi-off',
      pulse: false,
    },
    qr_pending: {
      color: 'bg-n-amber-9',
      pulseColor: 'bg-n-amber-9/40',
      textColor: 'text-n-amber-11',
      bgColor: 'bg-n-amber-3',
      label: t('INBOX_MGMT.DINASTIA_CONNECTION.QR_PENDING'),
      icon: 'i-lucide-qr-code',
      pulse: true,
    },
    connecting: {
      color: 'bg-n-amber-9',
      pulseColor: 'bg-n-amber-9/40',
      textColor: 'text-n-amber-11',
      bgColor: 'bg-n-amber-3',
      label: t('INBOX_MGMT.DINASTIA_CONNECTION.CONNECTING'),
      icon: 'i-lucide-loader-2',
      pulse: true,
    },
  };
  return (
    configs[connectionStatus.value] || {
      color: 'bg-n-slate-7',
      pulseColor: 'bg-n-slate-7/40',
      textColor: 'text-n-slate-10',
      bgColor: 'bg-n-slate-3',
      label: '...',
      icon: 'i-lucide-circle-dashed',
      pulse: false,
    }
  );
});

const isConnected = computed(() => connectionStatus.value === 'connected');
const isNotConnected = computed(() => connectionStatus.value !== 'connected');
const hasQrPending = computed(
  () => connectionStatus.value === 'qr_pending' && !!qrImage.value
);

const dialogShowLoading = computed(() => {
  if (connectionStatus.value === 'connected') return false;
  if (connectionStatus.value === 'disconnected') return false;
  if (qrImage.value) return false;
  return true;
});

const dialogShowQr = computed(
  () => !!qrImage.value && connectionStatus.value !== 'connected'
);

const dialogShowConnected = computed(
  () => connectionStatus.value === 'connected'
);

// ── API actions ────────────────────────────────────────────────────────

async function fetchStatusForced() {
  if (!props.inbox.id) return;
  try {
    const { data } = await dinastiaWhatsappChannel.getSessionStatus(
      props.inbox.id
    );
    connectionStatus.value = data.connection_status;
    phoneNumber.value = data.phone_number || props.inbox.phone_number || '';

    if (data.session_data?.qr) {
      await processQrData(data.session_data.qr);
    } else if (data.connection_status === 'connected') {
      qrImage.value = '';
    }
  } catch {
    // Silently ignore polling errors
  }
}

function startFallbackPolling() {
  if (pollTimer) return;
  pollTimer = setInterval(() => {
    if (!cableSubscription) {
      fetchStatusForced();
    }
  }, 30000);
}

async function fetchStatus() {
  if (!props.inbox.id) return;
  if (isInitialized && cableSubscription) return;

  isLoading.value = true;
  try {
    const { data } = await dinastiaWhatsappChannel.getSessionStatus(
      props.inbox.id
    );
    connectionStatus.value = data.connection_status;
    phoneNumber.value = data.phone_number || props.inbox.phone_number || '';

    if (data.session_data?.qr) {
      await processQrData(data.session_data.qr);
    } else if (data.connection_status === 'connected') {
      qrImage.value = '';
    }

    if (
      data.connection_status === 'connected' ||
      data.connection_status === 'disconnected'
    ) {
      stopPolling();
    }

    if (data.connection_status === 'connected') {
      autoCloseDialog();
    }
  } catch {
    connectionStatus.value = props.inbox.connection_status || 'disconnected';
  } finally {
    isLoading.value = false;
    isInitialized = true;
  }
}

function startCooldown() {
  actionCooldown.value = true;
  setTimeout(() => {
    actionCooldown.value = false;
  }, 10000);
}

async function handleConnect() {
  if (actionCooldown.value) return;
  isActionLoading.value = true;
  qrImage.value = '';
  try {
    await dinastiaWhatsappChannel.connect(props.inbox.id);
    connectionStatus.value = 'connecting';
    startFallbackPolling();
  } catch {
    connectionStatus.value = 'disconnected';
  } finally {
    isActionLoading.value = false;
    startCooldown();
  }
}

async function handleDisconnect() {
  if (actionCooldown.value) return;
  isActionLoading.value = true;
  try {
    await dinastiaWhatsappChannel.disconnect(props.inbox.id);
    connectionStatus.value = 'disconnected';
    qrImage.value = '';
    stopPolling();
  } catch {
    // keep current status
  } finally {
    isActionLoading.value = false;
    startCooldown();
  }
}

async function handleReconnect() {
  if (actionCooldown.value) return;
  isReconnecting.value = true;
  qrDialogRef.value?.open();
  qrImage.value = '';
  try {
    await dinastiaWhatsappChannel.disconnect(props.inbox.id);
    connectionStatus.value = 'disconnected';
    await delay(2000);
    await dinastiaWhatsappChannel.connect(props.inbox.id);
    connectionStatus.value = 'connecting';
    startFallbackPolling();
  } catch {
    useAlert(t('INBOX_MGMT.DINASTIA_CONNECTION.RECONNECT_FAILED'));
  } finally {
    isReconnecting.value = false;
    startCooldown();
  }
}

async function handleSyncHistory() {
  isSyncing.value = true;
  try {
    await dinastiaWhatsappChannel.syncHistory(props.inbox.id);
    useAlert(t('INBOX_MGMT.DINASTIA_CONNECTION.SYNC_STARTED'));
  } catch {
    useAlert(t('INBOX_MGMT.DINASTIA_CONNECTION.SYNC_FAILED'));
  } finally {
    isSyncing.value = false;
  }
}

function openQrDialog() {
  qrDialogRef.value?.open();
  handleConnect();
}

function handleDialogClose() {
  if (autoCloseTimer) {
    clearTimeout(autoCloseTimer);
    autoCloseTimer = null;
  }
  if (connectionStatus.value !== 'connected') {
    stopPolling();
  }
}

// ── Lifecycle ──────────────────────────────────────────────────────────

watch(
  () => props.inbox.id,
  newId => {
    if (newId) {
      isInitialized = false;
      fetchStatus();
      unsubscribeFromChannel();
      subscribeToChannel();
    }
  },
  { immediate: true }
);

onMounted(() => {
  subscribeToChannel();
});

onUnmounted(() => {
  stopPolling();
  unsubscribeFromChannel();
  if (autoCloseTimer) clearTimeout(autoCloseTimer);
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <SettingsSectionCard
      :title="$t('INBOX_MGMT.DINASTIA_CONNECTION.TITLE')"
      :description="$t('INBOX_MGMT.DINASTIA_CONNECTION.DESCRIPTION')"
    >
      <div class="flex flex-col gap-5">
        <!-- Loading State -->
        <div
          v-if="isLoading && !connectionStatus"
          class="flex items-center justify-center p-8"
        >
          <span class="i-lucide-loader-2 animate-spin text-2xl text-n-brand" />
        </div>
        <!-- Status Card -->
        <div
          v-else
          class="flex items-center justify-between rounded-xl border border-n-weak p-4"
          :class="statusConfig.bgColor"
        >
          <div class="flex items-center gap-3">
            <!-- Status Dot -->
            <div class="relative flex items-center justify-center">
              <span
                v-if="statusConfig.pulse"
                class="absolute size-3 rounded-full animate-ping"
                :class="statusConfig.pulseColor"
              />
              <span
                class="relative size-3 rounded-full"
                :class="statusConfig.color"
              />
            </div>

            <div class="flex flex-col">
              <span
                class="text-sm font-semibold"
                :class="statusConfig.textColor"
              >
                {{ statusConfig.label }}
              </span>
              <span
                v-if="phoneNumber && isConnected"
                class="text-xs text-n-slate-10"
              >
                {{ phoneNumber }}
              </span>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="flex gap-2">
            <!-- Not connected: show connect or view QR -->
            <NextButton
              v-if="hasQrPending"
              solid
              amber
              sm
              :label="$t('INBOX_MGMT.DINASTIA_CONNECTION.VIEW_QR')"
              icon="i-lucide-qr-code"
              @click="qrDialogRef?.open()"
            />
            <NextButton
              v-if="isNotConnected"
              solid
              teal
              sm
              :label="$t('INBOX_MGMT.DINASTIA_CONNECTION.REQUEST_QR')"
              :is-loading="isActionLoading"
              :disabled="actionCooldown"
              icon="i-lucide-plug"
              @click="openQrDialog"
            />
            <!-- Connected: reconnect, sync, disconnect -->
            <NextButton
              v-if="isConnected"
              outline
              slate
              sm
              :label="$t('INBOX_MGMT.DINASTIA_CONNECTION.RECONNECT')"
              :is-loading="isReconnecting"
              :disabled="actionCooldown"
              icon="i-lucide-refresh-cw"
              @click="handleReconnect"
            />
            <NextButton
              v-if="isConnected"
              outline
              slate
              sm
              :label="$t('INBOX_MGMT.DINASTIA_CONNECTION.SYNC_HISTORY')"
              :is-loading="isSyncing"
              icon="i-lucide-history"
              @click="handleSyncHistory"
            />
            <NextButton
              v-if="isConnected"
              outline
              ruby
              sm
              :label="$t('INBOX_MGMT.DINASTIA_CONNECTION.DISCONNECT')"
              :is-loading="isActionLoading"
              :disabled="actionCooldown"
              icon="i-lucide-unplug"
              @click="handleDisconnect"
            />
          </div>
        </div>
      </div>
    </SettingsSectionCard>

    <!-- Group Management Toggle (disabled — coming soon) -->
    <SettingsSectionCard
      :title="$t('INBOX_MGMT.DINASTIA_CONNECTION.GROUP_MANAGEMENT')"
    >
      <div class="flex items-center justify-between">
        <span class="text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.DINASTIA_CONNECTION.GROUP_MANAGEMENT') }}
        </span>
        <span
          v-tooltip="
            $t('INBOX_MGMT.DINASTIA_CONNECTION.GROUP_MANAGEMENT_COMING_SOON')
          "
        >
          <input
            type="checkbox"
            disabled
            class="opacity-50 cursor-not-allowed"
          />
        </span>
      </div>
    </SettingsSectionCard>

    <!-- QR Code Dialog -->
    <Dialog
      ref="qrDialogRef"
      :title="$t('INBOX_MGMT.DINASTIA_CONNECTION.QR_DIALOG_TITLE')"
      :description="$t('INBOX_MGMT.DINASTIA_CONNECTION.QR_DIALOG_DESCRIPTION')"
      width="md"
      :show-confirm-button="false"
      :show-cancel-button="false"
      @close="handleDialogClose"
    >
      <div class="flex flex-col items-center gap-6 py-2">
        <!-- Dialog: Loading -->
        <div
          v-if="dialogShowLoading"
          class="flex flex-col items-center gap-4 py-6"
        >
          <span class="i-lucide-loader-2 animate-spin text-4xl text-n-brand" />
          <p class="text-sm text-n-slate-10 mb-0">
            {{ $t('INBOX_MGMT.FINISH.DINASTIA_WAITING') }}
          </p>
        </div>

        <!-- Dialog: QR Code -->
        <div v-else-if="dialogShowQr" class="flex flex-col items-center gap-4">
          <div class="rounded-xl bg-white p-3 shadow-sm">
            <img :src="qrImage" alt="WhatsApp QR Code" class="size-64" />
          </div>
          <div class="flex items-center gap-1.5 text-xs text-n-slate-8">
            <span class="i-lucide-refresh-cw text-xs" />
            <span>{{ $t('INBOX_MGMT.FINISH.DINASTIA_QR_REFRESHES') }}</span>
          </div>
          <button
            type="button"
            class="text-xs text-n-brand hover:text-n-brand/80 transition-colors"
            @click="handleConnect"
          >
            {{ $t('INBOX_MGMT.DINASTIA_CONNECTION.REGENERATE_QR') }}
          </button>
        </div>

        <!-- Dialog: Connected -->
        <div
          v-else-if="dialogShowConnected"
          class="flex flex-col items-center gap-4 py-4"
        >
          <div
            class="flex items-center justify-center size-16 rounded-full bg-n-teal-3"
          >
            <span class="i-lucide-check text-3xl text-n-teal-11" />
          </div>
          <div class="text-center">
            <p class="text-base font-semibold text-n-teal-11 mb-1">
              {{ $t('INBOX_MGMT.DINASTIA_CONNECTION.CONNECTED_SUCCESS') }}
            </p>
            <p v-if="phoneNumber" class="text-sm text-n-slate-10 mb-0">
              {{ phoneNumber }}
            </p>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="flex justify-center">
          <NextButton
            v-if="dialogShowConnected"
            solid
            teal
            :label="$t('INBOX_MGMT.DINASTIA_CONNECTION.CLOSE')"
            class="w-full"
            @click="qrDialogRef?.close()"
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>
